import { Controller } from "stimulus";
import SignaturePad from "signature_pad";

export default class extends Controller {
  static targets = ["canvas", "clearButton", "dataField", "signatureDisplay", "submitButton"];

  connect() {
    console.log("Signature Pad controller connected");
    console.log("Canvas target available:", this.hasCanvasTarget);
    console.log("Data field target available:", this.hasDataFieldTarget);
    console.log("Clear button target available:", this.hasClearButtonTarget);
    
    this.initializeSignaturePad();
    this.setupEventListeners();
    this.setupFormChangeDetection();
  }

  disconnect() {
    if (this.signaturePad) {
      this.signaturePad.off();
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout);
    }
    this.removeFormChangeDetection();
  }

  initializeSignaturePad() {
    if (!this.hasCanvasTarget) {
      console.error("Canvas target not found");
      return;
    }

    console.log("Initializing signature pad...");
    const canvas = this.canvasTarget;

    // Set canvas size based on container
    this.resizeCanvas(canvas);

    // Initialize SignaturePad with onEnd callback for autosave
    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: "rgb(255, 255, 255)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 0.5,
      maxWidth: 2.5,
      throttle: 16, // ms between points
      minDistance: 5,
      velocityFilterWeight: 0.7,
      onEnd: () => {
        console.log('onEnd callback triggered');
        this.updateDataField();
        this.showClearButton();
        this.scheduleAutosave();
      }
    });

    console.log("SignaturePad instance created:", this.signaturePad);
    console.log("SignaturePad initialized successfully");

    // Handle window resize
    if (!this.resizeHandler) {
      this.resizeHandler = () => this.handleResize();
      window.addEventListener("resize", this.resizeHandler);
    }
  }

  resizeCanvas(canvas) {
    const container = canvas.parentElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    // Get the actual available width (accounting for padding and borders)
    const computedStyle = window.getComputedStyle(container);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingRight = parseFloat(computedStyle.paddingRight);
    const availableWidth = container.clientWidth - paddingLeft - paddingRight;

    // Set canvas dimensions
    canvas.width = availableWidth * ratio;
    canvas.height = 150 * ratio; // Match CSS height of 150px
    canvas.style.width = availableWidth + "px";
    canvas.style.height = "150px";

    const context = canvas.getContext("2d");
    context.scale(ratio, ratio);
  }

  handleResize() {
    // Save existing signature
    const data = this.signaturePad.toData();

    // Resize canvas
    this.resizeCanvas(this.canvasTarget);

    // Restore signature
    this.signaturePad.fromData(data);
  }

  setupEventListeners() {
    // Clear button is handled by data-action attribute in the view
    // No manual event listener needed here
  }

  clear(event) {
    console.log('Clear method called', event);
    if (event) {
      event.preventDefault();
    }
    
    if (this.signaturePad) {
      console.log('Clearing signature pad...');
      
      // Save the current signature data to check if it actually clears
      const wasEmpty = this.signaturePad.isEmpty();
      console.log('Was empty before clear:', wasEmpty);
      
      // Clear the signature pad
      this.signaturePad.clear();
      
      // Force a complete canvas redraw
      const canvas = this.canvasTarget;
      const context = canvas.getContext('2d');
      
      // Clear the entire canvas
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      // Fill with white background
      context.fillStyle = 'rgb(255, 255, 255)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Verify it's now empty
      const isEmptyNow = this.signaturePad.isEmpty();
      console.log('Is empty after clear:', isEmptyNow);
      
      // Update UI and data
      this.updateDataField();
      this.hideClearButton();
      this.enableFormEditing();
      this.hideSignedBanner();
      this.hideSignedTimestamp();
      
      // Cancel any pending autosave
      if (this.autosaveTimeout) {
        clearTimeout(this.autosaveTimeout);
        this.autosaveTimeout = null;
      }
      
      console.log('Signature cleared successfully');
    } else {
      console.error('SignaturePad instance not found');
    }
  }

  deleteSignature(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation(); // Prevent event from bubbling
      event.stopImmediatePropagation(); // Stop other handlers
    }

    console.log('deleteSignature called once');

    const confirmed = confirm(
      "⚠️ Delete Signature?\n\n" +
      "Are you sure you want to delete this saved signature?\n\n" +
      "This action cannot be undone and will require the supervisor to re-sign."
    );

    if (!confirmed) {
      console.log('Delete cancelled by user');
      return;
    }

    console.log('Deleting saved signature...');

    // Get the form
    const form = this.element.closest('form');
    if (!form) {
      console.error('Delete failed: form not found');
      alert('Error: Could not find form');
      return;
    }

    // Create FormData with empty signature to delete
    const formData = new FormData(form);
    formData.set('patient_encounter[supervisor_signature_data]', '');
    
    const url = form.action;
    const method = 'PATCH';

    console.log('Delete signature URL:', url);

    fetch(url, {
      method: method,
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json',
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (response.ok) {
        console.log('Signature deleted successfully');
        // Reload the page to show the empty signature state
        window.location.reload();
      } else {
        console.error('Delete failed with status:', response.status);
        alert('Error: Failed to delete signature');
      }
    })
    .catch(error => {
      console.error('Delete error:', error);
      alert('Error: Failed to delete signature');
    });
  }

  updateDataField() {
    if (!this.hasDataFieldTarget) {
      return;
    }

    if (this.signaturePad.isEmpty()) {
      this.dataFieldTarget.value = "";
    } else {
      // Convert to base64 PNG (without data URI prefix)
      const dataURL = this.signaturePad.toDataURL("image/png");
      const base64 = dataURL.split(",")[1];
      this.dataFieldTarget.value = base64;

      // Lock form after signature
      this.lockFormAfterSignature();
    }

    // Dispatch custom event for signature change
    this.element.dispatchEvent(
      new CustomEvent("signature-changed", {
        bubbles: true,
        detail: { isEmpty: this.signaturePad.isEmpty() },
      })
    );
  }

  scheduleAutosave() {
    console.log('scheduleAutosave called');
    
    // Cancel any existing autosave timeout
    if (this.autosaveTimeout) {
      console.log('Clearing existing autosave timeout');
      clearTimeout(this.autosaveTimeout);
    }

    // Wait 1.5 seconds after the last stroke before autosaving
    console.log('Setting autosave timeout for 1.5 seconds');
    this.autosaveTimeout = setTimeout(() => {
      console.log('Autosave timeout expired, calling performAutosave');
      this.performAutosave();
    }, 1500);
  }

  performAutosave() {
    // Check if signature pad has content
    if (!this.signaturePad || this.signaturePad.isEmpty()) {
      console.log('Autosave cancelled: signature is empty');
      return;
    }

    // Get the form
    const form = this.element.closest('form');
    if (!form) {
      console.error('Autosave failed: form not found');
      this.showSaveError();
      return;
    }

    console.log('Performing autosave...');

    // Show saving indicator
    this.showSavingIndicator();

    // Create FormData and submit via AJAX
    const formData = new FormData(form);
    const url = form.action;
    const method = form.method || 'POST';
    
    console.log('Autosave URL:', url);
    console.log('Autosave method:', method);

    fetch(url, {
      method: method,
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json', // Request JSON response to prevent redirect
      },
      credentials: 'same-origin'
    })
    .then(response => {
      console.log('Autosave response status:', response.status);
      console.log('Autosave response redirected:', response.redirected);
      
      if (response.ok || response.status === 200) {
        console.log('Autosave successful');
        this.showSaveSuccess();
        
        // Show submit button if it exists
        this.showSubmitButton();
        
        // Don't redirect - just stay on the page
        // The timestamp will show the user that it saved
      } else {
        console.error('Autosave failed with status:', response.status);
        response.text().then(text => {
          console.error('Response body:', text.substring(0, 500));
        });
        this.showSaveError();
      }
    })
    .catch(error => {
      console.error('Autosave error:', error);
      this.showSaveError();
    });
  }

  showSavingIndicator() {
    let indicator = document.getElementById('autosave-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.id = 'autosave-indicator';
      indicator.className = 'alert alert-info';
      indicator.style.cssText = 'position: fixed; top: 20px; right: 20px; z-index: 9999; padding: 0.75rem 1rem; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);';
      indicator.innerHTML = `
        <i class="fas fa-spinner fa-spin me-2"></i>
        <strong>Saving signature...</strong>
      `;
      document.body.appendChild(indicator);
    }
    indicator.classList.remove('d-none');
  }

  showSaveSuccess() {
    const indicator = document.getElementById('autosave-indicator');
    if (indicator) {
      indicator.className = 'alert alert-success';
      indicator.innerHTML = `
        <i class="fas fa-check-circle me-2"></i>
        <strong>Signature saved!</strong>
      `;
      setTimeout(() => {
        indicator.classList.add('fade');
        setTimeout(() => indicator.remove(), 300);
      }, 2000);
    }
    
    // Show timestamp in the signature section
    this.showSignedTimestamp();
  }
  
  showSignedTimestamp() {
    // Remove any existing timestamp
    const existingTimestamp = document.getElementById('signature-timestamp');
    if (existingTimestamp) {
      existingTimestamp.remove();
    }
    
    // Create timestamp element
    const timestamp = document.createElement('div');
    timestamp.id = 'signature-timestamp';
    timestamp.style.cssText = 'margin-top: 0.5rem; padding: 0.5rem; background: #d1fae5; border: 1px solid #10b981; border-radius: 6px; display: flex; align-items: center; gap: 0.375rem;';
    
    const now = new Date();
    const formattedTime = now.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    timestamp.innerHTML = `
      <i class="fas fa-check-circle" style="color: #10b981; font-size: 0.875rem;"></i>
      <span style="color: #065f46; font-size: 0.75rem; font-weight: 500;">
        Signed at: ${formattedTime}
      </span>
    `;
    
    // Insert after the signature canvas wrapper
    const canvasWrapper = this.element.querySelector('.signature-canvas-wrapper-compact');
    if (canvasWrapper && canvasWrapper.parentNode) {
      canvasWrapper.parentNode.insertBefore(timestamp, canvasWrapper.nextSibling);
    }
  }
  
  hideSignedTimestamp() {
    const timestamp = document.getElementById('signature-timestamp');
    if (timestamp) {
      timestamp.remove();
    }
  }

  showSaveError() {
    const indicator = document.getElementById('autosave-indicator');
    if (indicator) {
      indicator.className = 'alert alert-danger';
      indicator.innerHTML = `
        <i class="fas fa-exclamation-circle me-2"></i>
        <strong>Save failed. Please use the Save button.</strong>
      `;
      setTimeout(() => {
        indicator.classList.add('fade');
        setTimeout(() => indicator.remove(), 300);
      }, 4000);
    }
  }

  showClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.remove("d-none");
    }
  }

  hideClearButton() {
    // Don't hide the clear button - keep it visible at all times
    // Users should always be able to clear
    if (this.hasClearButtonTarget) {
      // this.clearButtonTarget.classList.add("d-none");
      console.log('Clear button visibility: keeping visible');
    }
  }

  // NEW: Form locking functionality
  setupFormChangeDetection() {
    // Store reference to form
    this.form = this.element.closest("form");
    if (!this.form) return;

    // Bind the handler so we can remove it later
    this.formChangeHandler = this.handleFormChange.bind(this);

    // Store previous values to allow reverting
    this.captureBeforeChangeHandler = this.captureBeforeChange.bind(this);

    // Listen for changes on all form inputs
    this.form.addEventListener("change", this.formChangeHandler);
    this.form.addEventListener("input", this.formChangeHandler);

    // Capture values before change for potential revert
    this.form.addEventListener("focus", this.captureBeforeChangeHandler, true);
  }

  removeFormChangeDetection() {
    if (this.form && this.formChangeHandler) {
      this.form.removeEventListener("change", this.formChangeHandler);
      this.form.removeEventListener("input", this.formChangeHandler);
      this.form.removeEventListener(
        "focus",
        this.captureBeforeChangeHandler,
        true
      );
    }
  }

  captureBeforeChange(event) {
    // Store the current value before user changes it
    const target = event.target;
    if (target.type === "checkbox" || target.type === "radio") {
      this.previousChecked = target.checked;
    } else if (target.type === "select-one" || target.tagName === "SELECT") {
      this.previousSelectIndex = target.selectedIndex;
    } else {
      this.previousValue = target.value;
    }
  }

  handleFormChange(event) {
    // Ignore changes to signature field itself
    if (event.target === this.dataFieldTarget) return;
    if (event.target === this.canvasTarget) return;
    if (event.target.closest("[data-controller='signature-pad']")) return;

    // If form has a signature, warn user before clearing it
    if (!this.signaturePad.isEmpty()) {
      // Prevent the change temporarily
      event.preventDefault();

      // Show confirmation dialog
      const confirmed = confirm(
        "⚠️ Warning: Form is Signed\n\n" +
          "This form has been signed by a supervisor. If you make changes, " +
          "the signature will be automatically cleared and the supervisor will " +
          "need to re-sign.\n\n" +
          "Do you want to continue with this change?"
      );

      if (confirmed) {
        // User confirmed - clear signature and allow the change
        this.clearSignatureOnFormChange();

        // Re-trigger the change on the element
        // This allows the actual value change to proceed
        if (event.target.type === "checkbox" || event.target.type === "radio") {
          // For checkboxes/radios, toggle the checked state
          event.target.checked = !event.target.checked;
          event.target.dispatchEvent(new Event("change", { bubbles: true }));
        } else {
          // For other inputs, the change will naturally proceed
          // Just need to allow it through
        }
      } else {
        // User cancelled - revert the change
        if (event.target.type === "checkbox" || event.target.type === "radio") {
          event.target.checked = !event.target.checked;
        } else if (
          event.target.type === "select-one" ||
          event.target.tagName === "SELECT"
        ) {
          // For selects, revert to previous value
          event.target.selectedIndex = this.previousSelectIndex || 0;
        } else {
          // For text inputs, revert to previous value
          event.target.value = this.previousValue || "";
        }
      }
    }
  }

  clearSignatureOnFormChange() {
    // Show warning banner
    this.showWarningBanner();

    // Clear signature
    this.signaturePad.clear();
    this.updateDataField();
    this.hideClearButton();
    this.enableFormEditing();
    this.hideSignedBanner();
  }

  lockFormAfterSignature() {
    if (!this.form) return;

    // Show signed banner
    this.showSignedBanner();

    // Don't actually disable fields, but show warning
    // This allows user to clear signature if they want to edit
  }

  enableFormEditing() {
    // Form is always editable, but clearing signature is required
    this.hideWarningBanner();
  }

  showSignedBanner() {
    let banner = document.getElementById("signature-locked-banner");
    if (!banner) {
      banner = document.createElement("div");
      banner.id = "signature-locked-banner";
      banner.className = "alert alert-success alert-dismissible fade show";
      banner.innerHTML = `
        <i class="fas fa-lock me-2"></i>
        <strong>Form Signed!</strong> 
        This encounter has been signed by the supervisor. 
        To make changes, click "Clear Signature" below and re-sign after editing.
      `;

      // Insert at top of form
      const formBody = this.form.querySelector(".card-body");
      if (formBody) {
        formBody.insertBefore(banner, formBody.firstChild);
      }
    }
    banner.classList.remove("d-none");
  }

  hideSignedBanner() {
    const banner = document.getElementById("signature-locked-banner");
    if (banner) {
      banner.classList.add("d-none");
    }
  }

  showWarningBanner() {
    let banner = document.getElementById("signature-cleared-warning");
    if (!banner) {
      banner = document.createElement("div");
      banner.id = "signature-cleared-warning";
      banner.className = "alert alert-warning alert-dismissible fade show";
      banner.innerHTML = `
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        <i class="fas fa-exclamation-triangle me-2"></i>
        <strong>Signature Cleared!</strong> 
        You modified the form, so the signature has been automatically removed. 
        Please have the supervisor re-sign after completing your changes.
      `;

      // Insert at top of form
      const formBody = this.form.querySelector(".card-body");
      if (formBody) {
        formBody.insertBefore(banner, formBody.firstChild);
      }
    }
    banner.classList.remove("d-none");

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (banner) {
        banner.classList.add("fade");
        setTimeout(() => banner.remove(), 150);
      }
    }, 5000);
  }

  hideWarningBanner() {
    const banner = document.getElementById("signature-cleared-warning");
    if (banner) {
      banner.remove();
    }
  }

  showSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      console.log('Showing submit button');
      this.submitButtonTarget.style.display = '';
    } else {
      // Try to find it by ID as fallback
      const submitBtn = document.getElementById('submit-for-review-btn');
      if (submitBtn) {
        console.log('Showing submit button (by ID)');
        submitBtn.style.display = '';
      }
    }
  }

  isEmpty() {
    return this.signaturePad ? this.signaturePad.isEmpty() : true;
  }

  // Method to validate signature before form submission
  validate() {
    if (this.isEmpty()) {
      return {
        valid: false,
        message: "Please provide a supervisor signature",
      };
    }
    return { valid: true };
  }
}
